import React, { ReactElement, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Body, Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { IBANFormItemRules } from 'common/utils/form.utils';
import { notifySuccess } from 'common/utils/notifier.utils';
import {
  useCreateCustomerAccountMutation,
  useCustomerAccountsListMutation,
} from 'domains/accounts/queries/accounts.query';
import { accountsAtom } from 'domains/accounts/store/accounts-store';
import { useGetAssetsListQuery } from 'domains/assets/queries/assets.query';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { useCustomersListQuery } from 'domains/customers/queries/customers.query';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { SContainer, SFooter, SSelectOption } from './modal.style';
import { EAccountManagementCustomerFormItemNames } from '../enums/customers.enum';

const { Option } = Select;

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
};

const CreateEditCustomersModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
  } = props;

  const navigate = useNavigate();

  const { t } = useTranslation();

  const [, setState] = useAtom(accountsAtom);

  const [form] = Form.useForm();

  const [selectedCounterpartyGuid, setSelectedCounterpartyGuid] = useState<string>('');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: counterparties } = useGetCounterpartiesQuery();

  const { data: customersData } = useCustomersListQuery(
    selectedCounterpartyGuid,
    {},
    {
      enabled: !!selectedCounterpartyGuid,
      refetchOnMount: false,
    },
  );

  const { data: assetsData } = useGetAssetsListQuery(selectedCounterpartyGuid, undefined, {
    enabled: !!selectedCounterpartyGuid,
    refetchOnMount: false,
  });

  const { mutateAsync: mutateAsyncCreateCustomerAccountMutation } = useCreateCustomerAccountMutation();

  const { mutateAsync: mutateAsyncGetCustomerAccountsMutation } = useCustomerAccountsListMutation();

  useEffect(() => {
    if (IS_BANK_FE && Object.keys(counterparties || {}).length) {
      form.setFields([
        {
          name: EAccountManagementCustomerFormItemNames.Counterparty,
          value: ENV_COUNTERPARTY_GUID,
        },
      ]);

      setSelectedCounterpartyGuid(ENV_COUNTERPARTY_GUID);
    }
  }, [counterparties]);

  const formValuesChangeHandler = async (value: any): Promise<void> => {
    if (EAccountManagementCustomerFormItemNames.Counterparty in value) {
      const counterpartyGUID = value.counterparty;
      setSelectedCounterpartyGuid(counterpartyGUID);
      form.setFieldValue(EAccountManagementCustomerFormItemNames.Customer, '');
      form.setFieldValue(EAccountManagementCustomerFormItemNames.Currency, '');
    }
  };

  const onCloseModalHandler = (): void => {
    onCancelModalHandler();
    navigate(ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS);
  };

  const onFormSubmitHandler = async (
    formValues: Record<EAccountManagementCustomerFormItemNames, string>
  ): Promise<void> => {
    try {
      setIsLoading(true);

      const {
        customer,
        iban,
        // TODO: need add this fields to request
        // currency,
      } = formValues;

      const createCustomerAccountReqBody: {
        counterpartyGUID: string;
        customerGUID: string;
        iban?: string;
      } = {
        counterpartyGUID: selectedCounterpartyGuid,
        customerGUID: customer,
      };

      if (iban) createCustomerAccountReqBody.iban = iban;

      const { wallet_account_guid } = await mutateAsyncCreateCustomerAccountMutation(createCustomerAccountReqBody);

      notifySuccess('accountManagement.customers.modal.create.notification.success');

      const updatedCustomerAccountsList = await mutateAsyncGetCustomerAccountsMutation({
        counterpartyGUID: ENV_COUNTERPARTY_GUID,
        customerGUID: customer,
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.AccountQueryKeys].getAccountsList._def,
      });

      setIsLoading(false);

      setState({
        updateTableStatus: true,
        accounts: updatedCustomerAccountsList?.accounts,
        accountNumber: wallet_account_guid,
      });

      onCancelModalHandler();

      navigate(ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS);

      // TODO: if need pass parameter Created
      // redirect(`../?${EQueryParams.Created}=true`);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <SContainer>
      <EnhancedModal open={isOpen} width={480} onCancel={onCloseModalHandler}>
        <Form
          form={form}
          onValuesChange={formValuesChangeHandler}
          onFinish={onFormSubmitHandler}
        >
          <Header>{t('accountManagement.customers.modal.create.title')}</Header>

          <Body>
            <Form.Item
              name={EAccountManagementCustomerFormItemNames.Counterparty}
              label={t('accountManagement.customers.modal.create.counterparty.label')}
            >
              <Select
                style={{
                  width: '100%',
                }}
                size="large"
                placeholder={t('accountManagement.customers.modal.create.counterparty.placeholder') as string}
                disabled={IS_BANK_FE}
              >
                {counterparties?.counterparties.map(({ guid, name }) => (
                  <Option key={guid} value={guid}>
                    <SSelectOption style={{ fontWeight: '500' }}>{name}</SSelectOption>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name={EAccountManagementCustomerFormItemNames.Customer}
              label={t('accountManagement.customers.modal.create.customer.label')}
            >
              <Select
                style={{
                  width: '100%',
                }}
                size="large"
                placeholder={t('accountManagement.customers.modal.create.customer.placeholder') as string}
              >
                {(customersData?.customers || []).map(({ wallet_user_guid, login }) => (
                  <Option key={wallet_user_guid} value={wallet_user_guid}>
                    <SSelectOption style={{ fontWeight: '500' }}>{login}</SSelectOption>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name={EAccountManagementCustomerFormItemNames.Currency}
              label={t('accountManagement.customers.modal.create.currency.label')}
            >
              <Select
                style={{
                  width: '100%',
                }}
                size="large"
                placeholder={t('accountManagement.customers.modal.create.currency.placeholder') as string}
              >
                {(assetsData?.stellar_assets || []).map(({ asset_code, asset_issuer }: any) => (
                  <Option key={asset_issuer} value={asset_code}>
                    <SSelectOption>{asset_code}</SSelectOption>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              rules={IBANFormItemRules}
              name={EAccountManagementCustomerFormItemNames.IBAN}
              label={t('accountManagement.customers.modal.create.iban.label')}
            >
              <Input
                placeholder={t('accountManagement.customers.modal.create.iban.placeholder') as string}
                size="large"
              />
            </Form.Item>
          </Body>
          <Footer>
            <SFooter>
              <Button
                style={{
                  marginRight: 12,
                }}
                onClick={onCloseModalHandler}
              >
                {t('accountManagement.customers.modal.create.button.cancel')}
              </Button>
              <Button loading={isLoading} htmlType="submit" type="primary">
                {t('accountManagement.customers.modal.create.button.create')}
              </Button>
            </SFooter>
          </Footer>
        </Form>
      </EnhancedModal>
    </SContainer>
  );
};

export default CreateEditCustomersModal;
